import dayjs from "dayjs";
import { useMemo } from "react";

import {
	Box,
	Button,
	Card,
	Center,
	Flex,
	Group,
	Space,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { Link } from "react-router";

import {
	useCaregiverAppAcceptPickupInvitationMutation,
	useCaregiverAppGetPickupInvitationsQuery,
} from "@/setup/frontend/api/apiHooks";
import { CaregiverAppSettingsButton } from "@/caregiver-app/components/CaregiverAppSettingsButton";
import { useAsyncNotifications } from "@/common/hooks/useAsyncNotifications";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);

export default function CaregiverAppPickupPage() {
	const pickupInvitationsQuery = useCaregiverAppGetPickupInvitationsQuery();
	const acceptPickupInvitationMutation =
		useCaregiverAppAcceptPickupInvitationMutation();

	useAsyncNotifications({
		isSuccess: acceptPickupInvitationMutation.isSuccess,
		isError: acceptPickupInvitationMutation.isError,
		error: {
			title: "Error accepting pickup invitation",
			color: "red",
			message: acceptPickupInvitationMutation.error?.message,
		},
		success: {
			title: "Pickup invitation accepted",
			color: "green",
			message: "You have accepted the pickup invitation.",
		},
	});

	const flattenedPickupInvitations = pickupInvitationsQuery.data ?? [];

	const dayHeadersMap = useMemo(() => {
		const map = new Map<number, string>();
		flattenedPickupInvitations.forEach((pickupInvitation, index) => {
			const prev = flattenedPickupInvitations[index - 1];
			if (
				!prev ||
				!dayjs
					.utc(prev.endTimestamp)
					.local()
					.isSame(dayjs.utc(pickupInvitation.startTimestamp).local(), "day")
			) {
				map.set(
					pickupInvitation.pickupInvitationId,
					dayjs.utc(pickupInvitation.startTimestamp).local().format("MMMM D"),
				);
			}
		});
		return map;
	}, [flattenedPickupInvitations]);

	return (
		<Flex direction="column" style={{ overflow: "hidden" }} flex={1}>
			<Box style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
				<Group h={64} px="md" justify="space-between">
					<CaregiverAppSettingsButton />
					<Title order={3} style={{ flex: 1, textAlign: "center" }}>
						Pickup Shifts
					</Title>
					<Space />
				</Group>
			</Box>
			<Flex direction="column" flex={1} style={{ overflow: "auto " }} p="sm">
				{flattenedPickupInvitations.map((pickupInvitation) => {
					const headerDateString = dayHeadersMap.get(
						pickupInvitation.pickupInvitationId,
					);
					return (
						<Stack key={pickupInvitation.pickupInvitationId}>
							{headerDateString ? (
								<Title order={3}>{headerDateString}</Title>
							) : null}
							<Card
								withBorder
								component="article"
								aria-label={pickupInvitation.clientName}
							>
								<Card.Section
									withBorder
									inheritPadding
									py="sm"
									className="base-100"
								>
									<Group justify="start">
										<Stack gap={4}>
											<Title size="h4">{pickupInvitation.clientName}</Title>
											<Group gap="xs">
												<Text>
													{dayjs
														.utc(pickupInvitation.startTimestamp)
														.local()
														.format("MMMM D, YYYY")}
												</Text>
											</Group>
										</Stack>
									</Group>
								</Card.Section>
								<Card.Section withBorder inheritPadding py="sm">
									<Stack>
										<Group>
											<Stack gap="sm" flex={1}>
												<Stack gap={0}>
													<Text fw={500} fz={14}>
														Scheduled In
													</Text>
													<Text>
														{dayjs
															.utc(pickupInvitation.startTimestamp)
															.local()
															.format("h:mm A")}
													</Text>
												</Stack>
											</Stack>
											<Stack gap="sm" flex={1}>
												<Stack gap={0}>
													<Text fw={500} fz={14}>
														Scheduled Out
													</Text>
													<Text>
														{dayjs
															.utc(pickupInvitation.endTimestamp)
															.local()
															.format("h:mm A")}
													</Text>
												</Stack>
											</Stack>
										</Group>
										<Group>
											<Stack flex={1} gap={0}>
												<Text fw={500} fz={14}>
													Description
												</Text>
												<Text>
													{pickupInvitation.description || "No Description"}
												</Text>
											</Stack>

											{Math.round(pickupInvitation.incentive) !== 0 && (
												<Stack flex={1} gap={0}>
													<Text fw={500} fz={14}>
														Incentive
													</Text>
													<Text>{Math.round(pickupInvitation.incentive)}</Text>
												</Stack>
											)}
										</Group>
										<Group>
											<Button
												flex={1}
												variant="light"
												component={Link}
												to={`https://www.google.com/maps/dir/?api=1&destination=${pickupInvitation.addressLatitude},${pickupInvitation.addressLongitude}`}
												target="_blank"
												rel="noopener noreferrer"
											>
												Open in Google Maps
											</Button>
											<Button
												flex={1}
												onClick={() =>
													acceptPickupInvitationMutation.mutate({
														id: pickupInvitation.pickupInvitationId,
													})
												}
											>
												Accept
											</Button>
										</Group>
									</Stack>
								</Card.Section>
							</Card>
						</Stack>
					);
				})}
				{flattenedPickupInvitations.length === 0 && (
					<Center h="100%">
						<Text>No pickup invitations found</Text>
					</Center>
				)}
			</Flex>
		</Flex>
	);
}
